import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Chip,
  Grid,
  Paper,
  CircularProgress,
  Alert,
  LinearProgress,
  Stack,
} from '@mui/material';
import {
  Receipt as ReceiptIcon,
  SmartToy as AIIcon,
  CheckCircle as ApproveIcon,
  Cancel as DeclineIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import CookieDetailPanel from './CookieDetailPanel';
import ApprovalConfirmDialog from './ApprovalConfirmDialog';
import DeclineConfirmDialog from './DeclineConfirmDialog';
import { commissionApi } from '../../services/api';

const getStatusChip = (status) => {
  const config = {
    pending: { label: 'Pending', color: 'warning' },
    auto_approved: { label: 'Auto Approved', color: 'success' },
    manually_approved: { label: 'Manually Approved', color: 'success' },
    declined: { label: 'Declined', color: 'default' },
  };
  const { label, color } = config[status] || { label: status, color: 'default' };
  return <Chip label={label} color={color} />;
};

const getMatchLevelChip = (level) => {
  const config = {
    PERFECT_MATCH: { label: 'Perfect Match', color: 'success' },
    COLOR_PRODUCT_MATCH: { label: 'Color + Product Match', color: 'info' },
    COLOR_RETAILER_MATCH: { label: 'Color + Retailer Match', color: 'warning' },
    RETAILER_ONLY_MATCH: { label: 'Retailer Only Match', color: 'error' },
    NO_MATCH: { label: 'No Match', color: 'default' },
  };
  const { label, color } = config[level] || { label: level, color: 'default' };
  return <Chip label={label} color={color} variant="outlined" />;
};

export default function CommissionDetailDialog({ open, item, onClose }) {
  const navigate = useNavigate();
  const [fullItem, setFullItem] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [declineDialogOpen, setDeclineDialogOpen] = useState(false);

  useEffect(() => {
    const fetchDetails = async () => {
      if (!item?.id || !open) return;

      try {
        setLoading(true);
        setError(null);
        const result = await commissionApi.get(item.id);
        if (result.success) {
          setFullItem(result.item);
        } else {
          setError(result.error || 'Failed to fetch details');
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchDetails();
  }, [item?.id, open]);

  const handleClose = (refreshNeeded = false) => {
    setFullItem(null);
    setError(null);
    onClose(refreshNeeded);
  };

  const handleApproveSuccess = () => {
    setApproveDialogOpen(false);
    handleClose(true);
  };

  const handleDeclineSuccess = () => {
    setDeclineDialogOpen(false);
    handleClose(true);
  };

  if (!open) return null;

  const displayItem = fullItem || item;
  const isPending = displayItem?.status === 'pending';
  const isApprovedNotInvoiced = (displayItem?.status === 'auto_approved' || displayItem?.status === 'manually_approved') && !displayItem?.invoice_id;
  const canDecline = isPending || isApprovedNotInvoiced;

  return (
    <>
      <Dialog open={open} onClose={() => handleClose(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Commission Details</Typography>
            {displayItem && getStatusChip(displayItem.status)}
          </Box>
        </DialogTitle>

        <DialogContent dividers>
          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {displayItem && !loading && (
            <Box>
              {/* Order Information (includes product details) */}
              <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Order Information
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="body2" color="text.secondary">
                      Order Number
                    </Typography>
                    <Typography variant="body1">
                      {displayItem.order_number || '-'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="body2" color="text.secondary">
                      Order Date
                    </Typography>
                    <Typography variant="body1">
                      {displayItem.order_timestamp
                        ? format(new Date(displayItem.order_timestamp), 'MMM d, yyyy HH:mm')
                        : '-'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="body2" color="text.secondary">
                      Price
                    </Typography>
                    <Typography variant="body1">
                      {displayItem.order_item_price != null
                        ? `${displayItem.order_item_price.toFixed(2)} ${displayItem.currency || 'EUR'}`
                        : '-'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="body2" color="text.secondary">
                      Match Level
                    </Typography>
                    {getMatchLevelChip(displayItem.match_level)}
                  </Grid>
                </Grid>

                {/* Product details within Order Information */}
                <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                  <Typography variant="body1" fontWeight="medium" sx={{ mb: 1 }}>
                    {displayItem.product_name || '-'}
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {displayItem.product_brand && (
                      <Chip label={displayItem.product_brand} size="small" />
                    )}
                    {displayItem.normalized_color && (
                      <Chip label={`Color: ${displayItem.normalized_color}`} size="small" variant="outlined" />
                    )}
                    {displayItem.product_size && (
                      <Chip label={`Size: ${displayItem.product_size}`} size="small" variant="outlined" />
                    )}
                  </Stack>
                </Box>
              </Paper>

              {/* Cookie Information - Full Width */}
              <CookieDetailPanel itemId={displayItem.id} />

              {/* AI Recommendation */}
              {displayItem.ai_recommendation && (
                <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'info.lighter' }}>
                  <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AIIcon color="info" /> AI Recommendation
                  </Typography>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={4}>
                      <Typography variant="body2" color="text.secondary">
                        Recommendation
                      </Typography>
                      <Chip
                        label={displayItem.ai_recommendation.recommendation?.toUpperCase()}
                        color={
                          displayItem.ai_recommendation.recommendation === 'approve'
                            ? 'success'
                            : displayItem.ai_recommendation.recommendation === 'decline'
                            ? 'error'
                            : 'warning'
                        }
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12} sm={8}>
                      <Typography variant="body2" color="text.secondary">
                        Confidence
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LinearProgress
                          variant="determinate"
                          value={(displayItem.ai_recommendation.confidence || 0) * 100}
                          sx={{ flexGrow: 1, height: 8, borderRadius: 4 }}
                        />
                        <Typography variant="body2">
                          {((displayItem.ai_recommendation.confidence || 0) * 100).toFixed(0)}%
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                  {displayItem.ai_recommendation.reasoning && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        Reasoning
                      </Typography>
                      <Typography variant="body2">
                        {displayItem.ai_recommendation.reasoning}
                      </Typography>
                    </Box>
                  )}
                </Paper>
              )}

              {/* Commission Details */}
              <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Commission Details
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6} sm={4}>
                    <Typography variant="body2" color="text.secondary">
                      Buying Retailer
                    </Typography>
                    <Typography variant="body1">
                      {displayItem.buying_retailer_name || displayItem.buying_retailer_id}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={4}>
                    <Typography variant="body2" color="text.secondary">
                      Selling Retailer
                    </Typography>
                    <Typography variant="body1">
                      {displayItem.selling_retailer_name || displayItem.selling_retailer_id}
                    </Typography>
                  </Grid>
                  <Grid item xs={4} sm={4}>
                    <Typography variant="body2" color="text.secondary">
                      Commission
                    </Typography>
                    <Typography variant="h6" color="primary">
                      {displayItem.commission_amount != null
                        ? `${displayItem.commission_amount.toFixed(2)} ${displayItem.currency || 'EUR'}`
                        : '-'}
                    </Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="body2" color="text.secondary">
                      Handoff Fee
                    </Typography>
                    <Typography variant="body1">
                      {displayItem.handoff_commission != null
                        ? `${displayItem.handoff_commission.toFixed(2)} ${displayItem.currency || 'EUR'}`
                        : '-'}
                    </Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="body2" color="text.secondary">
                      Conversion Time
                    </Typography>
                    <Typography variant="body1">
                      {displayItem.conversion_time_hours != null
                        ? `${displayItem.conversion_time_hours.toFixed(1)}h`
                        : '-'}
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>

              {/* Status Reason */}
              {displayItem.status_reason && (
                <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Status Reason
                  </Typography>
                  <Typography variant="body2">{displayItem.status_reason}</Typography>
                </Paper>
              )}

              {/* Invoice Info */}
              {displayItem.invoice_id && (
                <Paper
                  variant="outlined"
                  sx={{ p: 2, bgcolor: 'success.lighter', cursor: 'pointer' }}
                  onClick={() => navigate(`/invoicing?id=${displayItem.invoice_id}`)}
                >
                  <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ReceiptIcon color="success" /> Invoiced
                  </Typography>
                  <Typography variant="body2">
                    Invoice: {displayItem.invoice_number || displayItem.invoice_id.slice(0, 8)}
                  </Typography>
                  {displayItem.invoice_date && (
                    <Typography variant="caption" color="text.secondary">
                      Issued: {format(new Date(displayItem.invoice_date), 'MMM d, yyyy')}
                    </Typography>
                  )}
                </Paper>
              )}
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          {canDecline && (
            <Button
              color="error"
              startIcon={<DeclineIcon />}
              onClick={() => setDeclineDialogOpen(true)}
            >
              Decline
            </Button>
          )}
          {isPending && (
            <Button
              color="success"
              variant="contained"
              startIcon={<ApproveIcon />}
              onClick={() => setApproveDialogOpen(true)}
            >
              Approve
            </Button>
          )}
          <Button onClick={() => handleClose(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Approval Dialog */}
      <ApprovalConfirmDialog
        open={approveDialogOpen}
        item={displayItem}
        onClose={() => setApproveDialogOpen(false)}
        onSuccess={handleApproveSuccess}
      />

      {/* Decline Dialog */}
      <DeclineConfirmDialog
        open={declineDialogOpen}
        item={displayItem}
        onClose={() => setDeclineDialogOpen(false)}
        onSuccess={handleDeclineSuccess}
      />
    </>
  );
}
